<template>
  <div class="mt-5 overflow-hidden bg-white shadow-sm sm:rounded-lg">
    <!-- SUBSCRIPTION FEATURE HEADER-->
    <div class="flex flex-col flex-wrap gap-y-2 px-4 py-5 lg:flex-row">
      <p
        v-if="subscriptionFeature.id && subscriptionFeature.id !== 'new'"
        class="inline-flex items-center rounded-full bg-gray-100 px-2 text-xs leading-5 font-medium text-gray-700"
      >
        №{{ subscriptionFeature.id }}
      </p>

      <Listbox
        v-slot="{ open }"
        v-model="featureType"
        class="w-full"
        :disabled="disabled"
      >
        <div class="relative mt-2">
          <ListboxLabel class="block text-sm font-medium text-gray-700">
            {{ t('company.subscription.featureType') }}
          </ListboxLabel>
          <ListboxButton
            class="relative mt-1 min-h-[2.4rem] w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden disabled:bg-gray-100 sm:text-sm"
          >
            <span class="block truncate">{{ featureType.label }}</span>
            <span
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
            >
              <ChevronDownIcon
                class="mr-1 h-5 w-5 text-gray-400"
                :class="open ? 'rotate-180' : 'rotate-0'"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <transition
            leaveActiveClass="transition duration-100 ease-in"
            leaveFromClass="opacity-100"
            leaveToClass="opacity-0"
          >
            <ListboxOptions
              class="absolute z-1 max-h-60 w-full overflow-auto rounded-b-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
            >
              <ListboxOption value="" />
              <ListboxOption
                v-for="type in subscriptionStore.featureTypes"
                v-slot="{ active, selected }"
                :key="type.value"
                :value="type"
                as="template"
              >
                <li
                  :class="[
                    active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900',
                    'relative cursor-default px-4 py-2 select-none',
                  ]"
                >
                  <span
                    :class="[
                      selected ? 'font-medium' : 'font-normal',
                      'block truncate',
                    ]"
                    >{{ type.label }}</span
                  >
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>

      <Listbox
        v-slot="{ open }"
        v-model="selectedSites"
        class="my-5 w-full"
        :disabled="disabled"
        multiple
      >
        <div class="relative mt-2">
          <ListboxLabel class="block text-sm font-medium text-gray-700"
            >{{ t('company.subscription.site') }}
          </ListboxLabel>
          <ListboxButton
            class="relative mt-1 min-h-[2.4rem] w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden disabled:bg-gray-100 sm:text-sm"
          >
            <span class="block truncate">
              {{ selectedSites.map((site) => site.label).join(', ') }}
            </span>
            <span
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
            >
              <ChevronDownIcon
                class="mr-1 h-5 w-5 text-gray-400"
                :class="open ? 'rotate-180' : 'rotate-0'"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <transition
            leaveActiveClass="transition duration-100 ease-in"
            leaveFromClass="opacity-100"
            leaveToClass="opacity-0"
          >
            <ListboxOptions
              class="absolute z-1 max-h-60 w-full overflow-auto rounded-b-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
            >
              <ListboxOption value="" />
              <ListboxOption
                v-for="site in subscriptionStore.availableSites"
                v-slot="{ active, selected }"
                :key="site.value"
                :value="site"
                as="template"
              >
                <li
                  :class="[
                    active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900',
                    'relative cursor-default px-4 py-2 pl-10 select-none',
                  ]"
                >
                  <span
                    v-if="selected"
                    class="check-container absolute inset-y-0 left-0 flex items-center pl-3"
                  >
                    <CheckIcon
                      class="h-5 w-5 text-indigo-500"
                      aria-hidden="true"
                    />
                  </span>
                  <span
                    :class="[
                      selected ? 'font-medium' : 'font-normal',
                      'block truncate',
                    ]"
                    >{{ site.label }}</span
                  >
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
      <!-- First column-->
      <div class="text text-gray-900 sm:w-124">
        <div>
          {{ t(`company.subscription.${subscriptionFeature.slug}`) }}:
          <span class="font-semibold text-indigo-600">{{
            subscriptionFeature.resources
              .map((resource) => resource.name)
              .join(', ')
          }}</span>
        </div>
        <div class="mt-4 flex gap-x-4 text-xs">
          <div class="flex items-center">
            <CurrencyDollarIcon class="h-5 w-5 text-gray-600" />
            <label for="pay-as-you-go" class="mr-2 ml-1 block text-gray-600">{{
              t('company.subscription.payAsYouGo')
            }}</label>

            <Toggle
              srLabel="pay-as-you-go"
              :checked="subscriptionFeature.payAsYouGo"
              :onToggle="togglePayAsYouGo"
              :disabled="disabled"
              @click.stop
            />
          </div>
          <div class="flex items-center">
            <ArrowUturnLeftIcon class="h-4 w-4 text-gray-600" />
            <label for="reversible" class="mr-2 ml-1 block text-gray-600">{{
              t('company.subscription.reversible')
            }}</label>
            <Toggle
              srLabel="reversible"
              :checked="subscriptionFeature.reversible"
              :onToggle="toggleReversible"
              :disabled="disabled"
              @click.stop
            />
          </div>
        </div>
        <div class="mt-4 flex items-center gap-x-4 text-gray-600">
          <div class="flex items-center gap-x-1">
            <WalletIcon class="h-6 w-6" />
            <label class="text-sm" for="credits">{{
              t('company.subscription.credits')
            }}</label>
          </div>
          <div class="w-[13rem]" @click.stop>
            <FormInput
              id="credits"
              type="text"
              :value="subscriptionFeature.planCredits"
              :onInput="updateFeatureCredits"
              :rules="minValueRule"
              :disabled="disabled"
              inputClass="disabled:bg-gray-100"
            />
          </div>
        </div>
      </div>
      <!-- Secound column-->
      <div class="flex-col sm:pl-6">
        <div class="flex items-center gap-x-1 text-gray-600">
          <WalletIcon class="h-5 w-5" />
          <span>{{ t('company.subscription.creditUsage') }}</span>

          <button
            v-if="
              !disabled &&
              subscriptionFeature.id !== 'new' &&
              !subscriptionStore.isLoading &&
              subscription.state === 'ACTIVATED'
            "
            :disabled="subscriptionStore.isSaving"
            class="ml-2 flex items-center rounded-md bg-indigo-50 px-2.5 py-1.5 text-sm font-medium text-indigo-600 shadow-xs hover:bg-indigo-100 disabled:bg-gray-100 disabled:text-gray-500"
            @click="
              subscriptionStore.refreshSubscriptionFeaturesUsages(
                subscriptionFeature,
              )
            "
          >
            <ArrowPathIcon
              class="mr-1 h-4 w-4"
              :class="{ 'animate-spin': subscriptionStore.isSaving }"
            />
            {{ t('company.subscription.refreshUsages') }}
          </button>
        </div>
        <div class="mt-4 ml-1 flex flex-col gap-y-2 text-sm text-gray-500">
          <div>
            {{ t('company.subscription.creditUsed') }}:
            <span class="font-bold">{{
              subscriptionFeature.usedCredits || 0
            }}</span>
          </div>
          <div>
            {{ t('company.subscription.creditRemaining') }}:
            <span class="font-bold">{{
              subscriptionFeature.usedCredits
                ? subscriptionFeature.planCredits -
                  subscriptionFeature.usedCredits
                : subscriptionFeature.planCredits
            }}</span>
          </div>
          <div>
            {{ t('company.subscription.creditExceeded') }}:
            <span class="font-bold">{{
              subscriptionFeature.usedCredits > subscriptionFeature.planCredits
                ? subscriptionFeature.usedCredits -
                  subscriptionFeature.planCredits
                : 0
            }}</span>
          </div>
        </div>
      </div>

      <!-- Last column (collapse button)-->
      <div class="ml-auto flex items-center gap-4">
        <button
          v-show="subscriptionFeature?.usages?.length"
          ref="collapseButton"
          class="focus-visible:ring-indigo l-auto sm:mt-0:ml-auto mt-2 ml-auto flex items-center gap-x-2 text-indigo-500 focus:outline-hidden focus-visible:ring-2"
          @click.stop="
            () => {
              collapseUsagesPanel = !collapseUsagesPanel
            }
          "
        >
          <span class="font-medium">{{
            collapseUsagesPanel
              ? t('company.subscription.seeUsages')
              : t('company.subscription.hideUsages')
          }}</span>
          <ChevronDoubleUpIcon
            class="w-5 transition"
            :class="collapseUsagesPanel ? 'rotate-180' : ''"
          />
        </button>
        <button
          v-if="subscriptionFeature.id === 'new'"
          class="mt-2 ml-auto text-gray-900 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 sm:mt-0"
          @click.stop="removeSubscriptionFeature"
        >
          <TrashIcon class="w-5" />
        </button>
      </div>
    </div>
    <!-- SUBSCRIPTION_FEATURE USAGES-->
    <div
      v-if="subscriptionFeature?.usages?.length"
      class="overflow-hidden border-t border-gray-200 transition-[max-height] duration-500"
      :class="!collapseUsagesPanel ? 'h-fit max-h-fit' : 'h-0 max-h-0'"
    >
      <div class="divide-y divide-gray-200">
        <div v-for="usage in subscriptionFeature.usages" :key="usage.id">
          <SubscriptionFeatureUsage
            :usage="usage"
            :feature="subscriptionFeature"
            :reversible="!disabled && subscriptionFeature.reversible"
          />
        </div>
        <div class="px-5">
          <Pagination
            :page="subscriptionFeature.usagePage"
            :updatePageNumber="updatePageNumber"
            :updatePageSize="updatePageSize"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Toggle from '../Toggle.vue'
import FormInput from '../FormInput.vue'
import Pagination from '../Pagination.vue'
import HelpPopover from '../HelpPopover.vue'
import SubscriptionFeatureUsage from './SubscriptionFeatureUsage.vue'
import { useSubscriptionStore } from '../../store/subscriptionStore'
import { useI18n } from 'vue-i18n'
import { ref, computed } from 'vue'
import {
  ChevronDoubleUpIcon,
  CurrencyDollarIcon,
  ArrowUturnLeftIcon,
  WalletIcon,
  TrashIcon,
  ChevronDownIcon,
} from '@heroicons/vue/24/outline'
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  ListboxLabel,
} from '@headlessui/vue'
import { helpers, minValue } from '@vuelidate/validators'
import LoadingSpinner from '../LoadingSpinner.vue'
import { ArrowPathIcon, CheckIcon } from '@heroicons/vue/20/solid'

const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const subscriptionStore = useSubscriptionStore()

const minValueRule = {
  min: helpers.withMessage(
    t('company.subscription.validations.minCredits'),
    minValue(1),
  ),
}

const props = defineProps({
  subscriptionFeature: {
    required: true,
    type: Object,
  },
  subscription: {
    required: true,
    type: Object,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  index: {
    required: true,
    type: Number,
  },
})

const togglePayAsYouGo = (value) => {
  subscriptionStore.selectedSubscription.features[props.index].payAsYouGo =
    value
}

const toggleReversible = (value) => {
  subscriptionStore.selectedSubscription.features[props.index].reversible =
    value
}

const updateFeatureCredits = (value) => {
  subscriptionStore.selectedSubscription.features[props.index].planCredits =
    value
}

const removeSubscriptionFeature = () => {
  subscriptionStore.selectedSubscription.features.splice(props.index, 1)
}

const updatePageNumber = (value) => {
  subscriptionStore.selectedSubscription.features[
    props.index
  ].usagePage.number = value
  subscriptionStore.getSubscriptionFeaturesUsages()
}

const updatePageSize = (value) => {
  subscriptionStore.selectedSubscription.features[props.index].usagePage.size =
    value
  subscriptionStore.getSubscriptionFeaturesUsages()
}

const collapseUsagesPanel = ref(true)

const featureType = computed({
  get() {
    return (
      subscriptionStore.featureTypes.find(
        (type) => type.value === props.subscriptionFeature.slug,
      ) || {}
    )
  },
  set(newValue) {
    console.log(newValue)
  },
})

const selectedSites = computed({
  get() {
    return subscriptionStore.availableSites.filter(
      (site) =>
        !!props.subscriptionFeature.resources.find(
          (item) => item.id === site.value,
        ),
    )
  },
  set(newValue) {
    subscriptionStore.selectedSubscription.features[props.index].resources =
      newValue.map((item) => ({
        id: item.value,
        name: item.label,
        type: 'site',
      }))
  },
})
</script>
